'use client'

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'

interface RightPanelContextValue {
  content: ReactNode | null
  setContent: (node: ReactNode | null) => void
}

const RightPanelContext = createContext<RightPanelContextValue | null>(null)

export function RightPanelProvider({ children }: { children: ReactNode }) {
  const [content, setContentState] = useState<ReactNode | null>(null)
  const setContent = useCallback((node: ReactNode | null) => {
    setContentState(node)
  }, [])

  return (
    <RightPanelContext.Provider value={{ content, setContent }}>
      {children}
    </RightPanelContext.Provider>
  )
}

/**
 * Returns `setContent` — pages call this in a useEffect to inject
 * their right-panel content, and clean up on unmount.
 */
export function useRightPanel() {
  const ctx = useContext(RightPanelContext)
  if (!ctx)
    throw new Error('useRightPanel must be used within RightPanelProvider')
  return { setContent: ctx.setContent }
}

/**
 * Returns the current right-panel content — used by the layout shell.
 */
export function useRightPanelContent() {
  const ctx = useContext(RightPanelContext)
  if (!ctx)
    throw new Error(
      'useRightPanelContent must be used within RightPanelProvider',
    )
  return ctx.content
}
